/**
 * 2048 Game
 * Slide tiles and combine them to create a tile with 2048!
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useKeyboard } from './hooks/useKeyboard';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Game2048 = ({ onHome }) => {
  // Game State
  const [grid, setGrid] = useLocalStorage('game2048_grid', Array(16).fill(0));
  const [score, setScore] = useLocalStorage('game2048_score', 0);
  const [highScore, setHighScore] = useLocalStorage('game2048_highScore', 0);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.PLAYING);

  // Initialize game
  useEffect(() => {
    if (grid.every(cell => cell === 0)) {
      initGame();
    }
  }, []);

  // Keyboard controls
  useKeyboard((key) => {
    const moves = {
      'arrowup': 'up',
      'w': 'up',
      'arrowdown': 'down',
      's': 'down',
      'arrowleft': 'left',
      'a': 'left',
      'arrowright': 'right',
      'd': 'right',
    };

    if (moves[key] && gameState === GAME_CONFIG.states.PLAYING) {
      handleMove(moves[key]);
    }
  });

  // Utility functions
  const addNewTile = (currentGrid) => {
    const newGrid = [...currentGrid];
    const emptyIndices = newGrid
      .map((val, idx) => val === 0 ? idx : null)
      .filter(val => val !== null);

    if (emptyIndices.length > 0) {
      const randomIndex = emptyIndices[Math.floor(Math.random() * emptyIndices.length)];
      newGrid[randomIndex] = Math.random() < 0.9 ? 2 : 4;
    }
    return newGrid;
  };

  const gridToRows = (g) => [
    [g[0], g[1], g[2], g[3]],
    [g[4], g[5], g[6], g[7]],
    [g[8], g[9], g[10], g[11]],
    [g[12], g[13], g[14], g[15]],
  ];

  const rowsToGrid = (rows) => rows.flat();

  const slide = (row) => {
    return row.filter(val => val !== 0).concat(Array(4 - row.filter(val => val !== 0).length).fill(0));
  };

  const merge = (row) => {
    const merged = [0, 0, 0, 0];
    const slid = slide(row);
    let newScore = 0;

    for (let i = 0; i < 3; i++) {
      if (slid[i] !== 0 && slid[i] === slid[i + 1]) {
        merged[i] = slid[i] * 2;
        newScore += merged[i];
        slid.splice(i + 1, 1);
        slid.push(0);
      }
    }

    for (let i = 0; i < 4; i++) {
      if (merged[i] === 0) {
        merged[i] = slid[i];
      }
    }

    return [slide(merged), newScore];
  };

  const handleMove = (direction) => {
    let rows = gridToRows(grid);
    let newScore = score;
    let moved = false;

    if (direction === 'left' || direction === 'right') {
      if (direction === 'right') {
        rows = rows.map(r => r.reverse());
      }

      rows = rows.map(row => {
        const [mergedRow, rowScore] = merge(row);
        newScore += rowScore;
        return direction === 'right' ? mergedRow.reverse() : mergedRow;
      });

      if (direction === 'right') {
        rows = rows.map(r => r.reverse());
      }
    } else {
      // Transpose for up/down movement
      let cols = [];
      for (let i = 0; i < 4; i++) {
        cols.push([rows[0][i], rows[1][i], rows[2][i], rows[3][i]]);
      }

      if (direction === 'down') {
        cols = cols.map(c => c.reverse());
      }

      cols = cols.map(col => {
        const [mergedCol, colScore] = merge(col);
        newScore += colScore;
        return direction === 'down' ? mergedCol.reverse() : mergedCol;
      });

      if (direction === 'down') {
        cols = cols.map(c => c.reverse());
      }

      // Transpose back
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 4; j++) {
          rows[i][j] = cols[j][i];
        }
      }
    }

    const newGrid = rowsToGrid(rows);
    moved = !newGrid.every((val, idx) => val === grid[idx]);

    if (moved) {
      const updatedGrid = addNewTile(newGrid);
      setGrid(updatedGrid);
      setScore(newScore);

      // Check win condition
      if (updatedGrid.some(val => val === 2048)) {
        setGameState(GAME_CONFIG.states.WIN);
      }

      // Check lose condition
      if (!canMove(updatedGrid)) {
        setGameState(GAME_CONFIG.states.GAME_OVER);
      }

      // Update high score
      if (newScore > highScore) {
        setHighScore(newScore);
      }
    }
  };

  const canMove = (currentGrid) => {
    const rows = gridToRows(currentGrid);

    // Check if any cell is empty
    if (currentGrid.some(val => val === 0)) return true;

    // Check for possible merges
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        const idx = i * 4 + j;
        // Check right
        if (j < 3 && currentGrid[idx] === currentGrid[idx + 1]) return true;
        // Check down
        if (i < 3 && currentGrid[idx] === currentGrid[idx + 4]) return true;
      }
    }

    return false;
  };

  const initGame = () => {
    let newGrid = Array(16).fill(0);
    newGrid = addNewTile(newGrid);
    newGrid = addNewTile(newGrid);
    setGrid(newGrid);
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const getTileColor = (value) => {
    const colors = {
      0: '#e0e0e0',
      2: '#eee4da',
      4: '#ede0c8',
      8: '#f2b179',
      16: '#f59563',
      32: '#f67c5f',
      64: '#f65e3b',
      128: '#edcf72',
      256: '#edcc61',
      512: '#edc850',
      1024: '#edc53f',
      2048: '#edc22e',
    };
    return colors[value] || '#3c3c2f';
  };

  return (
    <GameLayout
      title="2048"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={initGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {/* Grid */}
        <div style={styles.gridContainer}>
          {grid.map((value, idx) => (
            <div
              key={idx}
              style={{
                ...styles.tile,
                backgroundColor: getTileColor(value),
                color: value > 4 ? '#f9f6f2' : '#776e65',
              }}
            >
              {value > 0 && <span style={styles.tileText}>{value}</span>}
            </div>
          ))}
        </div>

        {/* Game Status */}
        <div style={styles.statusArea}>
          {gameState === GAME_CONFIG.states.WIN && (
            <div style={styles.statusMessage}>
              🎉 You Reached 2048!
            </div>
          )}
          {gameState === GAME_CONFIG.states.GAME_OVER && (
            <div style={styles.statusMessage}>
              💀 No More Moves!
            </div>
          )}
          {gameState === GAME_CONFIG.states.PLAYING && (
            <div style={styles.instructions}>
              Use arrow keys or WASD to move
            </div>
          )}
        </div>

        {/* New Game Button */}
        <button
          onClick={initGame}
          className="btn btn-primary mt-3"
          aria-label="Start new game"
        >
          🔄 New Game
        </button>
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem',
    width: '100%',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 100px)',
    gap: '10px',
    padding: '15px',
    backgroundColor: '#bbada0',
    borderRadius: '8px',
    boxShadow: `0 0 20px ${GAME_CONFIG.colors.primary}`,
  },
  tile: {
    width: '100px',
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    fontWeight: 'bold',
    fontSize: '2rem',
    transition: 'all 150ms ease-in-out',
  },
  tileText: {
    textShadow: '0 0 10px rgba(0, 0, 0, 0.2)',
  },
  statusArea: {
    textAlign: 'center',
    minHeight: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusMessage: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  instructions: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
  },
};

export default Game2048;
